// The TipTap adapter for the `{Slicer.` menu and the editor props it needs.
//
// `editorProps` is built once: today's inline literal made `useEditor` call
// `editor.setOptions` on every render, because `compareOptions` compares every option
// except the `on*` callbacks by identity. `handleKeyDown` reads the hook through a ref, so
// the memoized object stays fresh. ProseMirror consults the view's own props before any
// plugin, so the menu answers Enter, Tab and the arrows before StarterKit's keymaps.

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { posToDOMRect, type Editor } from "@tiptap/react";
import type { EditorProps } from "@tiptap/pm/view";
import { textblockCaretText } from "../../Utils/slicerTokenTrigger.mjs";
import {
  optionId,
  useSlicerTokenTrigger,
  type SlicerTokenAdapter,
  type SlicerTokenTrigger,
} from "../shared/useSlicerTokenTrigger";

export interface TiptapSlicerMenu {
  /** Pass to `useEditor`; built once per editor. */
  editorProps: EditorProps;
  /** Subscribe the hook to an editor; returns the unsubscribe, for a `useEffect`. */
  attach(editor: Editor | null): () => void;
  trigger: SlicerTokenTrigger;
  /** The `position: relative` wrapper around `EditorContent` the anchor is placed in. */
  wrapperRef: React.RefObject<HTMLDivElement | null>;
  /** Where the anchor span sits inside the wrapper; null while the menu is closed. */
  anchorStyle: React.CSSProperties | null;
}

const ANCHOR_HEIGHT = 18;

export function useTiptapSlicerMenu(): TiptapSlicerMenu {
  const editorRef = useRef<Editor | null>(null);
  const triggerRef = useRef<SlicerTokenTrigger | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const adapter = useMemo<SlicerTokenAdapter>(
    () => ({
      caretText() {
        const editor = editorRef.current;
        if (!editor || editor.isDestroyed) return null;
        return textblockCaretText(editor.state.selection);
      },
      selection() {
        const editor = editorRef.current;
        if (!editor || editor.isDestroyed) return null;
        const { from, to } = editor.state.selection;
        return { from, to };
      },
      replace(from, to, text, caretOffset) {
        const editor = editorRef.current;
        if (!editor || editor.isDestroyed) return;
        // A tag-free string takes the text-only path of insertContentAt, which keeps the marks.
        editor
          .chain()
          .focus(undefined, { scrollIntoView: false })
          .insertContentAt({ from, to }, text)
          .setTextSelection(from + (caretOffset ?? text.length))
          .run();
      },
      caretRect() {
        const editor = editorRef.current;
        const menu = triggerRef.current?.menu;
        if (!editor || editor.isDestroyed || !menu?.open || !editor.view.dom.isConnected) return null;
        return posToDOMRect(editor.view, menu.brace, menu.brace);
      },
    }),
    [],
  );

  const trigger = useSlicerTokenTrigger(adapter);
  useLayoutEffect(() => {
    triggerRef.current = trigger;
  });

  const editorProps = useMemo<EditorProps>(
    () => ({
      attributes: { spellcheck: "false", "aria-autocomplete": "list" },
      handleKeyDown(_view, event) {
        // ProseMirror prevents the default on every Esc and Enter itself, so
        // `defaultPrevented` says nothing; only a key the menu handled stops propagation.
        const handled = triggerRef.current?.onKeyDown(event) ?? false;
        if (handled) {
          event.preventDefault();
          event.stopPropagation();
        }
        return handled;
      },
    }),
    [],
  );

  const attach = useCallback((editor: Editor | null) => {
    editorRef.current = editor;
    if (!editor) return () => {};
    // `transaction` fires for doc changes and caret moves, before the React re-render.
    const onTransaction = () => triggerRef.current?.refresh();
    const onBlur = () => triggerRef.current?.close();
    editor.on("transaction", onTransaction);
    editor.on("blur", onBlur);
    return () => {
      editor.off("transaction", onTransaction);
      editor.off("blur", onBlur);
      if (editorRef.current === editor) editorRef.current = null;
    };
  }, []);

  const { menu, model, listId } = trigger;
  const brace = menu.open ? menu.brace : -1;
  const activeIndex = menu.open ? menu.activeIndex : 0;

  // The contenteditable root carries the combobox state; `editorProps.attributes` is static.
  useEffect(() => {
    const dom = editorRef.current?.view.dom;
    if (!dom) return;
    dom.setAttribute("aria-expanded", String(menu.open));
    if (menu.open) dom.setAttribute("aria-controls", listId);
    else dom.removeAttribute("aria-controls");
    if (menu.open && model && model.pickable > 0) dom.setAttribute("aria-activedescendant", optionId(listId, activeIndex));
    else dom.removeAttribute("aria-activedescendant");
  }, [menu.open, model, listId, activeIndex]);

  // The anchor sits at the opening brace, so the list stays still while the coach types.
  const [anchorStyle, setAnchorStyle] = useState<React.CSSProperties | null>(null);
  useLayoutEffect(() => {
    if (brace < 0) {
      setAnchorStyle(null);
      return;
    }
    const rect = adapter.caretRect?.();
    const wrapper = wrapperRef.current?.getBoundingClientRect();
    if (!rect || !wrapper) {
      setAnchorStyle(null);
      return;
    }
    setAnchorStyle({
      left: rect.left - wrapper.left,
      top: rect.top - wrapper.top,
      height: rect.height || ANCHOR_HEIGHT,
    });
  }, [brace, adapter]);

  return { editorProps, attach, trigger, wrapperRef, anchorStyle };
}
